import * as THREE from 'three';
import { makeCanvas, prng } from './util';

function canvasTex(canvas: HTMLCanvasElement, srgb = true): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(canvas);
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  t.needsUpdate = true;
  return t;
}

/** Tileable grayscale grass detail (blades + clumps). Sampled at ~1.5 m repeat. */
export function grassDetailTexture(): THREE.CanvasTexture {
  const S = 256;
  const { canvas, ctx } = makeCanvas(S, S);
  const rnd = prng(1234);
  const img = ctx.createImageData(S, S);
  // value noise base
  const grid = 16;
  const g: number[] = [];
  for (let i = 0; i < grid * grid; i++) g.push(rnd());
  const at = (x: number, y: number) => g[((y % grid + grid) % grid) * grid + ((x % grid + grid) % grid)]!;
  for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
    const fx = (x / S) * grid, fy = (y / S) * grid;
    const ix = Math.floor(fx), iy = Math.floor(fy);
    const tx = fx - ix, ty = fy - iy;
    const sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
    const v = (at(ix, iy) * (1 - sx) + at(ix + 1, iy) * sx) * (1 - sy) + (at(ix, iy + 1) * (1 - sx) + at(ix + 1, iy + 1) * sx) * sy;
    const n = 0.55 + v * 0.3 + (rnd() - 0.5) * 0.35;
    const c = Math.max(0, Math.min(255, n * 255));
    const o = (y * S + x) * 4;
    img.data[o] = c; img.data[o + 1] = c; img.data[o + 2] = c; img.data[o + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  // short blade strokes
  ctx.globalAlpha = 0.25;
  for (let i = 0; i < 2600; i++) {
    const x = rnd() * S, y = rnd() * S, l = 2 + rnd() * 5, a = rnd() * Math.PI;
    ctx.strokeStyle = rnd() < 0.5 ? '#ffffff' : '#202020';
    ctx.lineWidth = 0.8;
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l); ctx.stroke();
  }
  const t = canvasTex(canvas, false);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

/** Large-scale variation (worn goalmouths etc. are analytic; this is patchiness). */
export function patchTexture(): THREE.CanvasTexture {
  const S = 128;
  const { canvas, ctx } = makeCanvas(S, S);
  const rnd = prng(99);
  ctx.fillStyle = '#808080'; ctx.fillRect(0, 0, S, S);
  for (let i = 0; i < 90; i++) {
    const x = rnd() * S, y = rnd() * S, r = 6 + rnd() * 22;
    const gr = ctx.createRadialGradient(x, y, 0, x, y, r);
    const v = rnd() < 0.5 ? 255 : 0;
    gr.addColorStop(0, `rgba(${v},${v},${v},0.18)`); gr.addColorStop(1, `rgba(${v},${v},${v},0)`);
    ctx.fillStyle = gr;
    for (const ox of [-S, 0, S]) for (const oy of [-S, 0, S]) { ctx.save(); ctx.translate(ox, oy); ctx.fillRect(x - r, y - r, r * 2, r * 2); ctx.restore(); }
  }
  const t = canvasTex(canvas, false);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

export function blobTexture(night: boolean): THREE.CanvasTexture {
  const S = 128;
  const { canvas, ctx } = makeCanvas(S, S);
  ctx.clearRect(0, 0, S, S);
  if (!night) {
    const gr = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
    gr.addColorStop(0, 'rgba(0,0,0,0.75)'); gr.addColorStop(0.45, 'rgba(0,0,0,0.5)'); gr.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gr; ctx.fillRect(0, 0, S, S);
  } else {
    // Four faint floodlight shadows in an X + a contact core.
    for (let k = 0; k < 4; k++) {
      ctx.save();
      ctx.translate(S / 2, S / 2); ctx.rotate(Math.PI / 4 + (k * Math.PI) / 2);
      const gr = ctx.createLinearGradient(0, 0, S / 2, 0);
      gr.addColorStop(0, 'rgba(0,0,0,0.32)'); gr.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = gr;
      ctx.beginPath(); ctx.ellipse(S / 4, 0, S / 4, S / 14, 0, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
    const gr = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 6);
    gr.addColorStop(0, 'rgba(0,0,0,0.6)'); gr.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gr; ctx.fillRect(0, 0, S, S);
  }
  return canvasTex(canvas, false);
}

export function glowTexture(): THREE.CanvasTexture {
  const S = 128;
  const { canvas, ctx } = makeCanvas(S, S);
  const gr = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  gr.addColorStop(0, 'rgba(255,255,255,1)');
  gr.addColorStop(0.12, 'rgba(255,250,235,0.9)');
  gr.addColorStop(0.35, 'rgba(255,240,210,0.25)');
  gr.addColorStop(1, 'rgba(255,240,210,0)');
  ctx.fillStyle = gr; ctx.fillRect(0, 0, S, S);
  return canvasTex(canvas);
}

export function netTexture(): THREE.CanvasTexture {
  const S = 32;
  const { canvas, ctx } = makeCanvas(S, S);
  ctx.clearRect(0, 0, S, S);
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(0, 1); ctx.lineTo(S, 1); ctx.moveTo(1, 0); ctx.lineTo(1, S); ctx.stroke();
  const t = canvasTex(canvas);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

/** 10×10 atlas of shirt numbers 0..99, white with dark outline (tinted per instance). */
export function numberAtlas(): THREE.CanvasTexture {
  const S = 1024, C = S / 10;
  const { canvas, ctx } = makeCanvas(S, S);
  ctx.clearRect(0, 0, S, S);
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (let n = 0; n < 100; n++) {
    const cx = (n % 10) * C + C / 2, cy = Math.floor(n / 10) * C + C / 2 + 4;
    const txt = String(n);
    const size = txt.length === 1 ? 84 : 70;
    ctx.font = `900 ${size}px "Arial Black", Impact, "Helvetica Neue", Arial, sans-serif`;
    ctx.save();
    ctx.translate(cx, cy); ctx.scale(txt.length === 1 ? 0.85 : 0.62, 1);
    ctx.lineWidth = 10; ctx.strokeStyle = 'rgba(20,20,20,1)'; ctx.lineJoin = 'round';
    ctx.strokeText(txt, 0, 0);
    ctx.fillStyle = '#ffffff'; ctx.fillText(txt, 0, 0);
    ctx.restore();
  }
  const t = canvasTex(canvas);
  t.anisotropy = 4;
  return t;
}

interface Brand { name: string; bg: string; fg: string; accent: string; logo: 'bolt' | 'circle' | 'chevron' | 'wave' | 'star' | 'diamond' }
const BRANDS: Brand[] = [
  { name: 'VOLTARA', bg: '#0b1d4a', fg: '#ffffff', accent: '#ffd500', logo: 'bolt' },
  { name: 'NORTHWIND AIR', bg: '#e8eef5', fg: '#0d2c6b', accent: '#d71920', logo: 'chevron' },
  { name: 'KESTREL BANK', bg: '#00553b', fg: '#ffffff', accent: '#9ee37d', logo: 'diamond' },
  { name: 'BRAVA COLA', bg: '#c4001a', fg: '#ffffff', accent: '#ffffff', logo: 'wave' },
  { name: 'ORBITEL', bg: '#5a1fb8', fg: '#ffffff', accent: '#38e1ff', logo: 'circle' },
  { name: 'HELIX TYRES', bg: '#111111', fg: '#ffcc00', accent: '#ffcc00', logo: 'star' },
  { name: 'PIXELFORGE', bg: '#ff5a1f', fg: '#ffffff', accent: '#1a1a1a', logo: 'diamond' },
  { name: 'SUMMIT WATCHES', bg: '#f4efe4', fg: '#1b1b1b', accent: '#b08d3c', logo: 'chevron' },
  { name: 'AURORA FOODS', bg: '#ffd84a', fg: '#7a1d00', accent: '#e8411f', logo: 'circle' },
  { name: 'CRESTLINE AUTO', bg: '#0f0f14', fg: '#ffffff', accent: '#ff2b4f', logo: 'chevron' },
  { name: 'LUMEN', bg: '#00a3e0', fg: '#ffffff', accent: '#ffffff', logo: 'star' },
  { name: 'TIDEWATER', bg: '#06324f', fg: '#7fe0ff', accent: '#7fe0ff', logo: 'wave' },
];

function drawLogo(ctx: CanvasRenderingContext2D, kind: Brand['logo'], x: number, y: number, s: number, color: string): void {
  ctx.fillStyle = color; ctx.strokeStyle = color; ctx.lineWidth = s * 0.18;
  ctx.beginPath();
  switch (kind) {
    case 'bolt':
      ctx.moveTo(x + s * 0.1, y - s * 0.5); ctx.lineTo(x - s * 0.35, y + s * 0.08); ctx.lineTo(x, y + s * 0.08);
      ctx.lineTo(x - s * 0.1, y + s * 0.5); ctx.lineTo(x + s * 0.35, y - s * 0.08); ctx.lineTo(x, y - s * 0.08); ctx.closePath(); ctx.fill(); break;
    case 'circle': ctx.arc(x, y, s * 0.4, 0, Math.PI * 2); ctx.stroke(); ctx.beginPath(); ctx.arc(x + s * 0.18, y - s * 0.18, s * 0.12, 0, Math.PI * 2); ctx.fill(); break;
    case 'chevron': ctx.moveTo(x - s * 0.4, y + s * 0.3); ctx.lineTo(x, y - s * 0.3); ctx.lineTo(x + s * 0.4, y + s * 0.3); ctx.stroke(); break;
    case 'wave':
      ctx.moveTo(x - s * 0.5, y); ctx.bezierCurveTo(x - s * 0.2, y - s * 0.5, x + s * 0.2, y + s * 0.5, x + s * 0.5, y); ctx.stroke(); break;
    case 'star':
      for (let i = 0; i < 10; i++) { const r = i % 2 ? s * 0.2 : s * 0.48; const a = -Math.PI / 2 + (i * Math.PI) / 5; ctx.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r); }
      ctx.closePath(); ctx.fill(); break;
    case 'diamond': ctx.moveTo(x, y - s * 0.45); ctx.lineTo(x + s * 0.35, y); ctx.lineTo(x, y + s * 0.45); ctx.lineTo(x - s * 0.35, y); ctx.closePath(); ctx.fill(); break;
  }
}

export const LED_ROWS = 8;
export const LED_ROW_GOAL_HOME = 6;
export const LED_ROW_GOAL_AWAY = 7;
/** One atlas row = 4 panels; one panel = PANEL_METRES of board length. */
export const LED_PANEL_METRES = 9;

/**
 * LED atlas: 8 rows × 4 panels (512×64 each). Rows 0..5 are sponsor rotations,
 * row 6/7 are "GOAL" sequences in home/away colours.
 */
export function ledAtlas(home: { name: string; short: string; primary: string; secondary: string }, away: { name: string; short: string; primary: string; secondary: string }): THREE.CanvasTexture {
  const PW = 512, PH = 64;
  const { canvas, ctx } = makeCanvas(PW * 4, PH * LED_ROWS);
  ctx.textBaseline = 'middle';
  const font = (px: number) => `900 ${px}px "Arial Black", Impact, "Helvetica Neue", Arial, sans-serif`;
  for (let row = 0; row < 6; row++) {
    for (let p = 0; p < 4; p++) {
      const b = BRANDS[(row * 4 + p * 3 + row) % BRANDS.length]!;
      const x0 = p * PW, y0 = row * PH;
      ctx.fillStyle = b.bg; ctx.fillRect(x0, y0, PW, PH);
      // styling variants
      if ((row + p) % 3 === 0) {
        ctx.fillStyle = b.accent; ctx.globalAlpha = 0.25;
        for (let k = 0; k < 6; k++) { ctx.beginPath(); ctx.moveTo(x0 + k * 90, y0 + PH); ctx.lineTo(x0 + k * 90 + 40, y0); ctx.lineTo(x0 + k * 90 + 60, y0); ctx.lineTo(x0 + k * 90 + 20, y0 + PH); ctx.fill(); }
        ctx.globalAlpha = 1;
      } else if ((row + p) % 3 === 1) {
        ctx.fillStyle = b.accent; ctx.fillRect(x0, y0 + PH - 7, PW, 7);
      }
      drawLogo(ctx, b.logo, x0 + 52, y0 + PH / 2, 44, b.accent);
      ctx.fillStyle = b.fg;
      ctx.font = font(38);
      const w = ctx.measureText(b.name).width;
      const sx = Math.min(1, 380 / w);
      ctx.save(); ctx.translate(x0 + 92, y0 + PH / 2 + 2); ctx.scale(sx, 1); ctx.fillText(b.name, 0, 0); ctx.restore();
    }
  }
  const goalRow = (row: number, t: { name: string; short: string; primary: string; secondary: string }) => {
    for (let p = 0; p < 4; p++) {
      const x0 = p * PW, y0 = row * PH;
      ctx.fillStyle = p % 2 ? t.secondary : t.primary; ctx.fillRect(x0, y0, PW, PH);
      ctx.fillStyle = p % 2 ? t.primary : t.secondary;
      for (let k = 0; k < 8; k++) { ctx.beginPath(); ctx.moveTo(x0 + k * 70, y0 + PH); ctx.lineTo(x0 + k * 70 + 30, y0); ctx.lineTo(x0 + k * 70 + 42, y0); ctx.lineTo(x0 + k * 70 + 12, y0 + PH); ctx.globalAlpha = 0.35; ctx.fill(); ctx.globalAlpha = 1; }
      ctx.font = font(44);
      const txt = p % 2 ? t.short.toUpperCase() : 'GOAL!';
      ctx.lineWidth = 5; ctx.strokeStyle = '#000000';
      const w = ctx.measureText(txt).width;
      ctx.strokeText(txt, x0 + (PW - w) / 2, y0 + PH / 2 + 2);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(txt, x0 + (PW - w) / 2, y0 + PH / 2 + 2);
    }
  };
  goalRow(LED_ROW_GOAL_HOME, home);
  goalRow(LED_ROW_GOAL_AWAY, away);
  const t = canvasTex(canvas);
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  t.repeat.set(1, 1 / LED_ROWS);
  return t;
}

